// The chain the page draws: the read of the root, with the reads of the
// opened cards merged into it, cut to the cards a column shows. Pure: it
// knows nothing of React or of the canvas.
//
// Nothing here counts: every number the page shows is a field of a response.
// A merged card keeps the fields of the read it was first found in.
//
// A read of both directions is two chains glued at the root: the hosts side
// is drawn leftwards, the parts side rightwards. A column is placed by its
// rank: its step on the parts side, minus its step on the hosts side, the
// root at 0.

import { EntityRef } from "../../types/productPlane";
import {
  ChainCard,
  ChainColumn,
  ChainDirection,
  ChainEdge,
  ChainPair,
  ChainSide,
  ChainTypical,
  ValueChain,
} from "../../types/valueChain";
import { ChainCardRef } from "./valueChainState";

// One column of the drawn chain
export interface DrawnColumn {
  // Steps from the root
  step: number;
  // The side of the root it is on
  side: ChainSide;
  // What most of its kinds hold, from the column of the read that brought
  // it; null when the read names none
  nature: string | null;
  // Its kinds hold more than one nature: the nature is what most hold
  mixed: boolean;
  // Its cards, kinds first, then classes, then the cards merged into it
  cardIds: string[];
}

// The read of the root with the opened cards merged in
export interface DrawnChain {
  root: ChainCard;
  // The direction the root was read in
  direction: ChainDirection;
  // The parts side, then the hosts side; by step within a side
  columns: DrawnColumn[];
  // Every card but the root, by id; `step` is the card's column in the drawn
  // chain
  cards: Map<string, ChainCard>;
  edges: ChainEdge[];
  // The edges left out of the read to break a cycle: drawn, muted
  brokenEdges: ChainEdge[];
}

// An edge as drawn: between two shown cards, or into a collapsed row
export interface DrawnEdge {
  id: string;
  source: string;
  target: string;
  // The edges of the reads it stands for: one, or several into a collapsed
  // row
  edges: ChainEdge[];
  // Into or out of a collapsed row
  collapsed: boolean;
  // A card reached directly from one two or more columns nearer the root
  direct: boolean;
  // Any edge it stands for is marked suspect by the read
  suspect: boolean;
  // Every edge it stands for rests on typical-host rows alone: no pair is
  // stated
  typical: boolean;
  // Left out of the read to break a cycle
  broken: boolean;
  // The intermediate module whose paths it is on, on the hosts side; null
  // for any other edge
  pathModule: string | null;
  // The side of the root it is on
  side: ChainSide;
}

// A row that stands for the cards of a column not shown, or the stub that
// stands for the whole parts side
export interface CollapsedRow {
  id: string;
  step: number;
  side: ChainSide;
  // The cards it stands for, in the column's order
  cards: ChainCard[];
  // The stub of the parts side: it opens the side, not one column
  stub: boolean;
  // The area whose cards it stands for, in a column grouped by area; null
  // for a whole column's row and for the stub
  area: string | null;
  // That area's name
  areaName: string | null;
}

// One area of a column before it is cut: its cards in order
export interface CardGroup {
  key: string;
  name: string;
  // What its kinds hold, from its first card; null when it names none
  nature: string | null;
  // Its place among the areas of the whole chain: a matrix gives each its
  // band in this order
  order: number;
  cards: ChainCard[];
}

// The cards of one area in a column grouped by area: the cards shown, then
// the row that stands for the rest
export interface VisibleGroup extends CardGroup {
  collapsed: CollapsedRow | null;
}

// How a column is grouped: its cards in, its areas out, in their order and
// each in its order. A column not grouped is cut as a whole.
export type ColumnGrouping = (cards: readonly ChainCard[]) => CardGroup[];

// The colour a module's paths are drawn in, by name
export type PathColour = "violet" | "green" | "pink" | "cyan" | "lime";

// The colours given to the intermediate modules, in the order they are met
export const PATH_COLOURS: readonly PathColour[] = [
  "violet",
  "green",
  "pink",
  "cyan",
  "lime",
];

// The drawn chain as the canvas shows it
export interface VisibleChain {
  root: ChainCard;
  // The direction the root was read in: both makes the root the focus
  direction: ChainDirection;
  columns: VisibleColumn[];
  edges: DrawnEdge[];
  // The colour of each intermediate module of the hosts side, by its id
  pathColours: ReadonlyMap<string, PathColour>;
}

export interface VisibleColumn {
  step: number;
  side: ChainSide;
  // Where it is placed: leftwards of the root for the hosts side
  rank: number;
  nature: string | null;
  mixed: boolean;
  // Its cards shown; in a column grouped by area, those of every group in
  // the groups' order
  cards: ChainCard[];
  collapsed: CollapsedRow | null;
  // Its areas, when it is grouped by area; empty otherwise
  groups: VisibleGroup[];
}

// Every row of a column that stands for cards not shown: the column's own,
// then each area's
export const rowsOf = (column: VisibleColumn): CollapsedRow[] => [
  ...(column.collapsed ? [column.collapsed] : []),
  ...column.groups.flatMap((group) => (group.collapsed ? [group.collapsed] : [])),
];

// Cards an area of a grouped column shows before the rest is collapsed into
// a row
export const AREA_CARD_LIMIT = 2;

// Areas a grouped column shows before the cards of the rest are collapsed
// into the column's own row
export const AREA_LIMIT = 6;

// What the maker highlight keeps lit: the cards and edges of its own paths
export interface Highlight {
  cards: ReadonlySet<string>;
  edges: ReadonlySet<string>;
}

// Cards a column shows before the rest is collapsed into a row
export const COLUMN_CARD_LIMIT = 8;

// Whether the canvas draws an edge that skips a column: into a card from a
// card two or more columns nearer the root. For now it does not. The card
// stays joined by the last edge of its longest path, which skips none, and
// what the cards say and the highlight still read every edge.
export const SKIP_EDGES_DRAWN = false;

// The name a card goes by: its label, else its id
export const cardName = (card: ChainCard): string => card.label ?? card.id;

// The id of the stub that stands for the parts side
export const PARTS_STUB_ID = "stub:parts";

// Where a column is placed: its step on the parts side, its step leftwards
// on the hosts side
export const columnRank = (side: ChainSide, step: number): number =>
  side === "hosts" ? -step : step;

// The id of the collapsed row of a column, by its rank; of an area's row in
// a grouped column, by the rank and the area
export const collapsedRowId = (rank: number, area?: string): string =>
  area === undefined ? `collapsed:${rank}` : `collapsed:${rank}:${area}`;

// What most of a column's kinds hold: the nature with the most kinds
export const dominantNature = (column: ChainColumn): string | null => {
  let best: string | null = null;
  let most = -1;
  for (const [nature, kinds] of Object.entries(column.natures)) {
    if (kinds > most) {
      best = nature;
      most = kinds;
    }
  }
  return best;
};

// Whether a column's kinds hold more than one nature
export const mixedNatures = (column: ChainColumn): boolean =>
  Object.values(column.natures).filter((kinds) => kinds > 0).length > 1;

const edgeKey = (edge: { host: string; part: string }): string =>
  `${edge.host}→${edge.part}`;

// Whether an edge of a read rests on typical-host rows alone
export const typicalOnly = (
  edge: Pick<ChainEdge, "pairs" | "typical">,
): boolean => edge.pairs === 0 && edge.typical > 0;

// Whether a card's products are stated as a class
const holdsClass = (card: ChainCard, classId: string): boolean =>
  card.classes.some((stated) => stated.category.id === classId);

// The read of the root as a drawn chain
export const drawChain = (chain: ValueChain): DrawnChain => {
  const cards = new Map(chain.cards.map((card) => [card.id, card]));
  return {
    root: chain.root,
    direction: chain.direction,
    columns: chain.columns.map((column) => ({
      step: column.step,
      side: column.side,
      nature: dominantNature(column),
      mixed: mixedNatures(column),
      cardIds: [...column.cards, ...column.unplaced].filter((id) =>
        cards.has(id),
      ),
    })),
    cards,
    edges: [...chain.edges],
    brokenEdges: [...chain.broken_edges],
  };
};

// The card a drawn chain holds under an id, the root included
const cardOf = (drawn: DrawnChain, id: string): ChainCard | undefined =>
  drawn.root.id === id ? drawn.root : drawn.cards.get(id);

// A column of the drawn chain, made when it is missing
const columnAt = (
  columns: DrawnColumn[],
  side: ChainSide,
  step: number,
  nature: string | null,
  mixed: boolean = false,
): DrawnColumn => {
  let column = columns.find(
    (existing) => existing.side === side && existing.step === step,
  );
  if (!column) {
    column = { step, side, nature, mixed, cardIds: [] };
    columns.push(column);
    columns.sort(
      (a, b) =>
        Number(a.side === "hosts") - Number(b.side === "hosts") ||
        a.step - b.step,
    );
  }
  return column;
};

// Merges the read of an opened card into a drawn chain. The opened card is
// found in the chain, or, for a stated class, beside the card whose products
// are stated as it: it is then drawn in that card's column, after it. The
// parts the read finds are drawn rightwards of a card on the parts side or
// of the root; a card on the hosts side has its parts in the chain already.
// A read of the hosts (the tree of what the root is built into) draws the
// hosts it finds leftwards of a card on the hosts side or of the root. A
// read with no place in the chain is left out.
export const mergeOpened = (
  drawn: DrawnChain,
  opened: ChainCardRef,
  read: ValueChain,
): DrawnChain => {
  const columns = drawn.columns.map((column) => ({
    ...column,
    cardIds: [...column.cardIds],
  }));
  const cards = new Map(drawn.cards);
  const edges = [...drawn.edges];
  const merged: DrawnChain = {
    root: drawn.root,
    direction: drawn.direction,
    columns,
    cards,
    edges,
    brokenEdges: drawn.brokenEdges,
  };

  let anchor = cardOf(merged, read.root.id) ?? cardOf(merged, opened.id);
  if (!anchor && opened.type === "class") {
    const holder = [drawn.root, ...Array.from(drawn.cards.values())].find((card) =>
      holdsClass(card, opened.id),
    );
    if (!holder) {
      return drawn;
    }
    anchor = { ...read.root, step: holder.step, side: holder.side };
    cards.set(anchor.id, anchor);
    // Beside the root, the class opens a column of its own at step 0
    const column = columnAt(columns, holder.side ?? "parts", holder.step, null);
    const at = column.cardIds.indexOf(holder.id);
    column.cardIds.splice(
      at < 0 ? column.cardIds.length : at + 1,
      0,
      anchor.id,
    );
  }
  if (!anchor) {
    return drawn;
  }
  const offset = anchor.step;
  const side: ChainSide = read.direction === "hosts" ? "hosts" : "parts";

  const otherSide: ChainSide = side === "parts" ? "hosts" : "parts";
  if (anchor.side !== otherSide) {
    const readColumns = new Map(
      read.columns
        .filter((column) => column.side === side)
        .map((column) => [column.step, column]),
    );
    for (const card of read.cards) {
      if (cardOf(merged, card.id) || card.side === otherSide) {
        continue;
      }
      const step = offset + card.step;
      const readColumn = readColumns.get(card.step);
      cards.set(card.id, { ...card, step, side });
      columnAt(
        columns,
        side,
        step,
        readColumn ? dominantNature(readColumn) : null,
        readColumn ? mixedNatures(readColumn) : false,
      ).cardIds.push(card.id);
    }
  }

  // The read names its root by the id it was asked with or by the id of its
  // row; its edges are drawn from the card the chain holds
  const keys = new Set(edges.map(edgeKey));
  for (const edge of read.edges) {
    const host = edge.host === read.root.id ? anchor.id : edge.host;
    const part = edge.part === read.root.id ? anchor.id : edge.part;
    const drawnEdge = { ...edge, host, part };
    if (!keys.has(edgeKey(drawnEdge))) {
      keys.add(edgeKey(drawnEdge));
      edges.push(drawnEdge);
    }
  }
  return merged;
};

// The read of the root with the reads of the opened cards merged in, in the
// order they were opened. A read not yet arrived is passed as undefined.
export const drawMergedChain = (
  chain: ValueChain,
  opened: readonly ChainCardRef[],
  reads: readonly (ValueChain | undefined)[],
): DrawnChain =>
  opened.reduce((drawn, ref, index) => {
    const read = reads[index];
    return read ? mergeOpened(drawn, ref, read) : drawn;
  }, drawChain(chain));

// The intermediate modules of the hosts side, each with its colour: the
// cards a host path comes through, in the order they are met
export const pathColoursOf = (
  cards: Iterable<ChainCard>,
): Map<string, PathColour> => {
  const colours = new Map<string, PathColour>();
  for (const card of Array.from(cards)) {
    if (card.side !== "hosts") {
      continue;
    }
    for (const path of card.paths) {
      if (path.via && !colours.has(path.via)) {
        colours.set(path.via, PATH_COLOURS[colours.size % PATH_COLOURS.length]);
      }
    }
  }
  return colours;
};

// The cards of one area as shown: the first `limit` and the opened ones,
// and a row naming the rest, unless the row is shown whole
const cutGroup = (
  group: CardGroup,
  column: DrawnColumn,
  rank: number,
  shownWhole: ReadonlySet<string>,
  keep: ReadonlySet<string>,
  limit: number,
): VisibleGroup => {
  const id = collapsedRowId(rank, group.key);
  if (group.cards.length <= limit || shownWhole.has(id)) {
    return { ...group, cards: [...group.cards], collapsed: null };
  }
  const shown = group.cards.filter(
    (card, index) => index < limit || keep.has(card.id),
  );
  return {
    ...group,
    cards: shown,
    collapsed: {
      id,
      step: column.step,
      side: column.side,
      cards: group.cards.filter((card) => !shown.includes(card)),
      stub: false,
      area: group.key,
      areaName: group.name,
    },
  };
};

// The drawn chain as shown: a column of more than `limit` cards shows the
// first ones and the opened ones, and a row naming the rest, unless that row
// is shown whole (by its id). With a grouping, each column's cards are
// grouped by area: the first `AREA_LIMIT` areas, and those holding an opened
// card, are each cut the same way at `AREA_CARD_LIMIT`, and the cards of the
// other areas go into the column's own row; the root's column is never
// grouped. With `partsCollapsed`, the whole parts side is one stub. Edges
// into a card not shown go into its row.
export const visibleChain = (
  drawn: DrawnChain,
  shownWhole: ReadonlySet<string>,
  keep: ReadonlySet<string>,
  limit: number = COLUMN_CARD_LIMIT,
  partsCollapsed: boolean = false,
  grouping: ColumnGrouping | null = null,
): VisibleChain => {
  // Where each card is drawn: itself, or the row of its column
  const drawnAs = new Map<string, string>([[drawn.root.id, drawn.root.id]]);
  const cardsOf = (column: DrawnColumn): ChainCard[] =>
    column.cardIds
      .map((id) => drawn.cards.get(id))
      .filter((card): card is ChainCard => card !== undefined);

  const partsColumns = drawn.columns.filter(
    (column) => column.side === "parts" && column.step > 0,
  );
  const stubbed =
    partsCollapsed && partsColumns.some((column) => cardsOf(column).length > 0);

  const columns: VisibleColumn[] = [];
  if (stubbed) {
    const stub: CollapsedRow = {
      id: PARTS_STUB_ID,
      step: 1,
      side: "parts",
      cards: partsColumns.flatMap(cardsOf),
      stub: true,
      area: null,
      areaName: null,
    };
    stub.cards.forEach((card) => drawnAs.set(card.id, stub.id));
    columns.push({
      step: 1,
      side: "parts",
      rank: 1,
      nature: null,
      mixed: false,
      cards: [],
      collapsed: stub,
      groups: [],
    });
  }

  for (const column of drawn.columns) {
    if (stubbed && partsColumns.includes(column)) {
      continue;
    }
    const rank = columnRank(column.side, column.step);
    const all = cardsOf(column);
    const base = {
      step: column.step,
      side: column.side,
      rank,
      nature: column.nature,
      mixed: column.mixed,
    };
    if (grouping && rank !== 0) {
      const allGroups = grouping(all);
      const whole = shownWhole.has(collapsedRowId(rank));
      const shownGroups = allGroups.filter(
        (group, index) =>
          whole ||
          index < AREA_LIMIT ||
          group.cards.some((card) => keep.has(card.id)),
      );
      const groups = shownGroups.map((group) =>
        cutGroup(group, column, rank, shownWhole, keep, AREA_CARD_LIMIT),
      );
      for (const group of groups) {
        group.cards.forEach((card) => drawnAs.set(card.id, card.id));
        group.collapsed?.cards.forEach((card) =>
          drawnAs.set(card.id, group.collapsed!.id),
        );
      }
      const hidden = allGroups
        .filter((group) => !shownGroups.includes(group))
        .flatMap((group) => group.cards);
      const row: CollapsedRow | null =
        hidden.length > 0
          ? {
              id: collapsedRowId(rank),
              step: column.step,
              side: column.side,
              cards: hidden,
              stub: false,
              area: null,
              areaName: null,
            }
          : null;
      hidden.forEach((card) => drawnAs.set(card.id, row!.id));
      columns.push({
        ...base,
        cards: groups.flatMap((group) => group.cards),
        collapsed: row,
        groups,
      });
      continue;
    }
    if (all.length <= limit || shownWhole.has(collapsedRowId(rank))) {
      all.forEach((card) => drawnAs.set(card.id, card.id));
      columns.push({ ...base, cards: all, collapsed: null, groups: [] });
      continue;
    }
    const shown = all.filter(
      (card, index) => index < limit || keep.has(card.id),
    );
    const hidden = all.filter((card) => !shown.includes(card));
    const row: CollapsedRow = {
      id: collapsedRowId(rank),
      step: column.step,
      side: column.side,
      cards: hidden,
      stub: false,
      area: null,
      areaName: null,
    };
    shown.forEach((card) => drawnAs.set(card.id, card.id));
    hidden.forEach((card) => drawnAs.set(card.id, row.id));
    columns.push({ ...base, cards: shown, collapsed: row, groups: [] });
  }

  const pathColours = pathColoursOf(drawn.cards.values());
  const sideOf = (id: string): ChainSide | null =>
    drawn.cards.get(id)?.side ?? null;
  // The module whose paths an edge of the hosts side is on: the module it
  // runs into, else the module it runs out of
  const moduleOf = (edge: ChainEdge): string | null => {
    if (sideOf(edge.host) !== "hosts" || edge.span > 1 || edge.span === 0) {
      return null;
    }
    if (pathColours.has(edge.part)) {
      return edge.part;
    }
    return pathColours.has(edge.host) ? edge.host : null;
  };

  const byPair = new Map<string, DrawnEdge>();
  const add = (edge: ChainEdge, broken: boolean) => {
    const source = drawnAs.get(edge.host);
    const target = drawnAs.get(edge.part);
    if (!source || !target || source === target) {
      return;
    }
    const collapsed = source !== edge.host || target !== edge.part;
    const id = `${source}→${target}${broken ? ":broken" : ""}`;
    const existing = byPair.get(id);
    if (existing) {
      existing.edges.push(edge);
      existing.direct = existing.direct && edge.span > 1;
      existing.suspect = existing.suspect || edge.suspect;
      existing.typical = existing.typical && typicalOnly(edge);
      if (existing.pathModule !== moduleOf(edge)) {
        existing.pathModule = null;
      }
    } else {
      byPair.set(id, {
        id,
        source,
        target,
        edges: [edge],
        collapsed,
        direct: !broken && edge.span > 1,
        suspect: edge.suspect,
        typical: typicalOnly(edge),
        broken,
        pathModule: collapsed ? null : moduleOf(edge),
        side: sideOf(edge.host) === "hosts" ? "hosts" : "parts",
      });
    }
  };
  drawn.edges.forEach((edge) => add(edge, false));
  drawn.brokenEdges.forEach((edge) => add(edge, true));

  return {
    root: drawn.root,
    direction: drawn.direction,
    columns,
    edges: Array.from(byPair.values()),
    pathColours,
  };
};

// The cards a card is stated with, next to it on the side of the root: its
// hosts on the parts side, its parts on the hosts side. Collapsed rows and
// broken edges are left out.
export const neighboursOf = (
  visible: VisibleChain,
  card: ChainCard,
): ChainCard[] => {
  const byId = new Map<string, ChainCard>([[visible.root.id, visible.root]]);
  for (const column of visible.columns) {
    for (const shown of column.cards) {
      byId.set(shown.id, shown);
    }
  }
  const hosts = card.side === "hosts";
  const found: ChainCard[] = [];
  for (const edge of visible.edges) {
    if (edge.broken || edge.collapsed) {
      continue;
    }
    const near = hosts
      ? edge.source === card.id
        ? edge.target
        : null
      : edge.target === card.id
        ? edge.source
        : null;
    const neighbour = near ? byId.get(near) : undefined;
    if (neighbour && !found.includes(neighbour)) {
      found.push(neighbour);
    }
  }
  return found;
};

// What the focus card says of the hosts it goes into: the kinds in the
// furthest column of the hosts side, what most of them hold, the
// intermediate modules they are reached through, whether one is reached
// directly, and the hosts nearer than that column that no path runs through
export interface FocusSummary {
  furthest: number;
  nature: string | null;
  modules: number;
  direct: boolean;
  others: number;
}

// The rank of the furthest column of the hosts side; null without one
export const furthestHostRank = (visible: VisibleChain): number | null => {
  const ranks = visible.columns
    .filter((column) => column.side === "hosts" && column.cards.length > 0)
    .map((column) => column.rank);
  return ranks.length > 0 ? Math.min(...ranks) : null;
};

// The focus summary of a chain read both ways; null for another direction
// or without a hosts side
export const focusSummary = (visible: VisibleChain): FocusSummary | null => {
  const rank = furthestHostRank(visible);
  if (visible.direction !== "both" || rank === null) {
    return null;
  }
  const furthest = visible.columns.find((column) => column.rank === rank)!;
  const nearer = visible.columns.filter(
    (column) => column.side === "hosts" && column.rank !== rank,
  );
  return {
    furthest:
      furthest.cards.length +
      rowsOf(furthest).reduce((total, row) => total + row.cards.length, 0),
    nature: furthest.nature,
    modules: visible.pathColours.size,
    direct: furthest.cards.some((card) =>
      card.paths.some((path) => path.via === null),
    ),
    others: nearer
      .flatMap((column) => column.cards)
      .filter((card) => !visible.pathColours.has(card.id)).length,
  };
};

// The stated pairs of a drawn edge, with the articles of each
export const statedPairs = (edge: DrawnEdge): ChainPair[] =>
  edge.edges.flatMap((stated) => stated.stated_pairs);

// The typical-host rows of a drawn edge, with the articles of each
export const typicalRows = (edge: DrawnEdge): ChainTypical[] =>
  edge.edges.flatMap((stated) => stated.typical_rows);

// The makers a highlight may be set to: the organizations proven on a card
// of the hosts side, by name
export const highlightMakers = (chain: ValueChain): EntityRef[] => {
  const makers = new Map<string, EntityRef>();
  for (const card of chain.cards) {
    if (card.side !== "hosts") {
      continue;
    }
    for (const maker of card.makers) {
      if (maker.standing === "proven") {
        makers.set(maker.organization.id, maker.organization);
      }
    }
  }
  return Array.from(makers.values()).sort((a, b) =>
    (a.name ?? a.id).localeCompare(b.name ?? b.id),
  );
};

// What a maker's highlight keeps lit: the root, the cards with a product of
// the maker in their proven set, and the edges between two lit cards. Null
// without a maker.
export const highlightOf = (
  visible: VisibleChain,
  maker: string | null,
): Highlight | null => {
  if (!maker) {
    return null;
  }
  const cards = new Set<string>([visible.root.id]);
  for (const column of visible.columns) {
    for (const card of column.cards) {
      if (
        card.makers.some(
          (candidate) =>
            candidate.organization.id === maker &&
            candidate.standing === "proven",
        )
      ) {
        cards.add(card.id);
      }
    }
  }
  const edges = new Set(
    visible.edges
      .filter((edge) => cards.has(edge.source) && cards.has(edge.target))
      .map((edge) => edge.id),
  );
  return { cards, edges };
};
